import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import '../../assets/css/buttons.css';

import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/Context';

import { SocketContext } from '../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import {
  headerCellSx,
  dataRowSx,
  touchMenuProps,
} from '../../theme/tableTokens';

import TableLoader from '../../components/TableLoader/TableLoader';
import TouchButton from '../../components/TouchButton/TouchButton';


// Touchscreen variant of headerCellSx for modal tables
const modalHeaderCellSx = {
  ...headerCellSx,
  fontSize: '17px',
  padding: '18px 12px',
};

// Time only: the plan is always one day, so the date adds nothing here
const formatDateStart = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

// Secondary detail under a cell's main value, still legible standing up
const subLineSx = {
  fontSize: '15px',
  color: '#8fa3d4',
  mt: 0.25,
};

// Compact TouchButton size for in-table actions
const rowButtonSx = {
  minWidth: '90px',
  height: '58px',
  fontSize: '19px',
  px: 2,
};

// Card-price tiers, same color language as the papir sale screens
const PRICE_TIERS = { 10: '#22d3ee', 15: '#4ade80', 20: '#c084fc' };
const DEFAULT_PRICE_COLOR = '#8fa3d4';

const priceCardColor = (price) =>
  PRICE_TIERS[Number(price)] || DEFAULT_PRICE_COLOR;

// Card prices from config.priceCard, flat priceCard as legacy fallback
const getGamePrices = (row) => {
  const raw = row?.config?.priceCard;
  const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const nums = list.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  if (!nums.length && row?.priceCard != null && Number(row.priceCard) > 0) {
    nums.push(Number(row.priceCard));
  }
  return Array.from(new Set(nums)).sort((a, b) => a - b);
};

function Spillplan() {
  const { hall, printer } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { isLogin, isBlocking, BlockingScreen } = useContext(AppContext);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const [rows, setRows] = useState(false);
  const [rowsFiltered, setRowsFiltered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [winnerModal, setWinnerModal] = useState(null);

  const [gameType, setGameType] = useState('superbingo');

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') event.preventDefault();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isLogin) {
      navigate('/login');
    }
  }, []);

  useEffect(() => {
    socket.emit('getRopPW', { action: 'getRopPW' });
  }, [hall]);

  useEffect(() => {
    const handleGetRopPW = (data) => {
      setRows(data);
      setRowsFiltered(data);
      setIsLoading(false);
    };

    const handleGetRopKingo = (data) => {
      setRows(data);
      setRowsFiltered(data);
      setIsLoading(false);
    };

    const handleGetRopXtipp = (data) => {
      setRows(data);
      setRowsFiltered(data);
      setIsLoading(false);
    };

    socket.on('getRopPW', handleGetRopPW);
    socket.on('getRopKingo', handleGetRopKingo);
    socket.on('getRopXtipp', handleGetRopXtipp);
    return () => {
      socket.off('getRopPW', handleGetRopPW);
      socket.off('getRopKingo', handleGetRopKingo);
      socket.off('getRopXtipp', handleGetRopXtipp);
    };
  }, [socket]);

  useEffect(() => {
    setIsLoading(true);
    setWinnerModal(null);
    switch (gameType) {
      case 'superbingo':
        socket.emit('getRopPW', { action: 'getRopPW', provider: gameType });
        break;
      case 'kingo':
        socket.emit('getRopKingo', {
          action: 'getRopKingo',
          provider: gameType,
        });
        break;
      case 'xtipp':
        socket.emit('getRopXtipp', {
          action: 'getRopXtipp',
          provider: gameType,
        });
        break;
      default:
        break;
    }
  }, [gameType]);

  // Printing allowed only once winning.dateEnd ("hh:mm:ss") has passed today; mirrors the backend
  const isGameFinished = (row) => {
    const endTimeStr = row?.winning?.dateEnd;
    if (!endTimeStr) return false;
    const [h, m, s] = endTimeStr.split(':').map(Number);
    const now = new Date();
    const endToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      h || 0,
      m || 0,
      s || 0,
      0
    );
    return now >= endToday;
  };

  const handlePrintGame = (gameID, name, numbers, prices) => {
    console.log('handlePrintGame called with:', {
      gameID,
      name,
      numbers,
      prices,
      printer: printerRef.current,
      game:
        gameType === 'superbingo'
          ? 'PowerBingo'
          : gameType === 'kingo'
            ? 'Kingo'
            : 'Xtipp',
    });
    socket.emit('printDrawGamePlan', {
      gameID: gameID,
      name: name,
      numbers: numbers,
      priceCard: prices,
      printer: printerRef.current,
      game:
        gameType === 'superbingo'
          ? 'PowerBingo'
          : gameType === 'kingo'
            ? 'Kingo'
            : 'Xtipp',
    });
  };

  return (
    <>
      {isBlocking && <BlockingScreen />}

      {/* WINNERS MODAL */}
      {winnerModal && (
        <Dialog
          open
          onClose={() => setWinnerModal(null)}
          // lg scrolled sideways once the person columns arrived; 96vw like the end-shift modal
          maxWidth={false}
          fullWidth
          PaperProps={{ sx: { width: '96vw', maxWidth: '1900px', m: 0 } }}
        >
          <DialogTitle
            sx={{ borderBottom: '1px solid #2a3a6e', px: 4, pt: 3, pb: 2.5 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: '32px', fontWeight: 700 }}>
                {winnerModal.name
                  ? `${winnerModal.name} — ${t('gameplan.winners')}`
                  : t('gameplan.winners')}
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'rgba(74,222,128,0.12)',
                  border: '2px solid #4ade80',
                  borderRadius: '8px',
                  color: '#4ade80',
                  fontSize: '30px',
                  fontWeight: 900,
                  px: 3,
                  py: 1,
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                }}
              >
                {winnerModal.winning.winners.length}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 5, mt: 2.5, flexWrap: 'wrap' }}>
              {[
                [t('gameplan.gameID'), winnerModal.gameID],
                // dateStart is a row field; `winning` only carries gameID/dateEnd/winners
                [t('gameplan.dateStart'), formatDateStart(winnerModal.dateStart)],
                [t('gameplan.dateEnd'), winnerModal.winning.dateEnd],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#8fa3d4',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '26px', fontWeight: 700, color: '#fcad89' }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 4, pt: 3 }}>
            <Box
              sx={{
                maxHeight: '52vh',
                overflow: 'auto',
                // muted scrollbar; sp-scroll's #365ab8 thumb reads as a blue rule here
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: '#2a3a6e',
                  borderRadius: '6px',
                },
              }}
            >
              <Table
                stickyHeader
                sx={{
                  '& tbody tr:nth-of-type(odd)': {
                    backgroundColor: '#232a45',
                  },
                  '& tbody tr:nth-of-type(even)': {
                    backgroundColor: '#1a2036',
                  },
                  '& tbody td': {
                    color: 'white',
                    fontSize: '22px',
                    padding: '18px 12px',
                    borderBottom: '1px solid #2a3a6e',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {gameType === 'superbingo' && (
                      <TableCell align="center" sx={modalHeaderCellSx}>
                        {t('gameplan.prizeType')}
                      </TableCell>
                    )}
                    {gameType === 'superbingo' && (
                      <TableCell align="center" sx={modalHeaderCellSx}>
                        {t('gameplan.rop')}
                      </TableCell>
                    )}
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.cardNumber')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.priceCard')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.userID')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.username')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.firstname')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.lastname')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.phone')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.bingoName')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.playFrom')}
                    </TableCell>
                    <TableCell align="center" sx={modalHeaderCellSx}>
                      {t('gameplan.win')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {winnerModal.winning.winners.map((w, wi) => (
                    <TableRow key={wi}>
                      {gameType === 'superbingo' && (
                        <TableCell
                          align="center"
                          sx={{ color: '#fcad89 !important', fontWeight: 700 }}
                        >
                          {w.prizeType}
                        </TableCell>
                      )}
                      {gameType === 'superbingo' && (
                        <TableCell align="center">{w.rop}</TableCell>
                      )}
                      <TableCell align="center">{w.cardNumber}</TableCell>
                      {/* No purchase row for the card means unknown, not free */}
                      <TableCell
                        align="center"
                        sx={{
                          color: w.priceCard
                            ? `${priceCardColor(w.priceCard)} !important`
                            : undefined,
                          fontWeight: 700,
                        }}
                      >
                        {w.priceCard ? `${w.priceCard} kr` : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {w.userID}
                        {/* The winning row held a child ticket id, not the member's */}
                        {w.viaTicketID && (
                          <Box sx={subLineSx}>
                            {t('gameplan.viaTicket')} → {w.memberID}
                          </Box>
                        )}
                      </TableCell>
                      {/* No username means nobody identified; on paper sales that is a guest */}
                      <TableCell align="center">
                        {w.username || (
                          <span style={{ color: '#8fa3d4', fontStyle: 'italic' }}>
                            {t('gameplan.guest')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="center">{w.firstname || '—'}</TableCell>
                      <TableCell align="center">{w.lastname || '—'}</TableCell>
                      <TableCell align="center">{w.phonenumber || '—'}</TableCell>
                      <TableCell align="center">{w.bingoName}</TableCell>
                      <TableCell align="center">{w.playFrom}</TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: '#4ade80 !important', fontWeight: 700 }}
                      >
                        {w.win} kr
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              borderTop: '1px solid #2a3a6e',
              px: 4,
              py: 3,
              justifyContent: 'center',
            }}
          >
            <TouchButton
              tone="confirm"
              onClick={() => setWinnerModal(null)}
              startIcon={<CheckCircleIcon />}
              sx={{ minWidth: '200px' }}
            >
              {t('sales.close')}
            </TouchButton>
          </DialogActions>
        </Dialog>
      )}

      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <Grid
            container
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {/* GAME SELECTOR */}
            <Grid
              item
              xs={12}
              sx={{
                height: '120px',
                maxHeight: '120px',
                display: 'flex',
                alignItems: 'center',
                px: 3,
                borderBottom: '1px solid #2a3a6e',
              }}
            >
              <FormControl
                sx={{
                  minWidth: 260,
                  backgroundColor: '#232a45',
                  borderRadius: '4px',
                }}
              >
                <InputLabel
                  sx={{ color: 'white !important', fontSize: '20px' }}
                >
                  {t('sales.provider')}
                </InputLabel>
                <MuiSelect
                  MenuProps={touchMenuProps}
                  value={gameType}
                  label={t('sales.provider')}
                  onChange={(e) => setGameType(e.target.value)}
                  sx={{
                    color: 'white',
                    height: '72px',
                    fontSize: '22px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#365ab8',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fcad89',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fcad89',
                    },
                    '& .MuiSvgIcon-root': { color: 'white' },
                  }}
                >
                  <MenuItem value="superbingo">PowerBingo</MenuItem>
                  <MenuItem value="kingo">Kingo</MenuItem>
                  <MenuItem value="xtipp">Xtipp</MenuItem>
                </MuiSelect>
              </FormControl>
            </Grid>

            {/*TABLE*/}
            <Grid
              item
              xs={12}
              sx={{
                width: '100%',
                padding: '10px',
                height: '100%',
              }}
            >
              {isLoading ? (
                <TableLoader />
              ) : (
                <TableContainer
                  component={Paper}
                  className="sp-scroll"
                  sx={{
                    maxHeight: 'calc(100% - 120px)',
                    marginBottom: '120px',
                    backgroundColor: '#1a2240',
                  }}
                >
                  <Table
                    sx={{
                      color: 'white',
                      background: '#1a2240',
                    }}
                    stickyHeader
                    aria-label="simple table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={headerCellSx}>
                          {t('gameplan.dateStart')}
                        </TableCell>
                        <TableCell align="center" sx={headerCellSx}>
                          {t('gameplan.gameID')}
                        </TableCell>
                        {gameType === 'superbingo' && (
                          <TableCell align="center" sx={headerCellSx}>
                            {t('gameplan.name')}
                          </TableCell>
                        )}
                        {gameType === 'superbingo' && (
                          <TableCell align="center" sx={headerCellSx}>
                            {t('gameplan.priceCard')}
                          </TableCell>
                        )}
                        <TableCell align="center" sx={headerCellSx}>
                          {t('gameplan.winners')}
                        </TableCell>
                        <TableCell align="center" sx={headerCellSx}></TableCell>
                      </TableRow>
                    </TableHead>
                    {rowsFiltered && (
                      <TableBody>
                        {rowsFiltered.map((row, index) => {
                          const hasWinners = row.winning?.winners?.length > 0;
                          const finished = isGameFinished(row);
                          const prices = getGamePrices(row);

                          return (
                            <TableRow
                              key={index}
                              sx={dataRowSx}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                                align="center"
                                sx={{
                                  color: '#8fa3d4',
                                  fontWeight: 700,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {row.dateStart
                                  ? new Date(row.dateStart).toLocaleTimeString(
                                      'no-NO',
                                      {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                      }
                                    )
                                  : '—'}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  color: '#fcad89',
                                  fontWeight: 700,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {row.gameID}
                              </TableCell>
                              {gameType === 'superbingo' && (
                                <TableCell
                                  align="center"
                                  sx={{ color: 'white', fontWeight: 700 }}
                                >
                                  {row.name || '—'}
                                </TableCell>
                              )}
                              {gameType === 'superbingo' && (
                                <TableCell align="center">
                                  {prices.length ? (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: 'center',
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      {prices.map((price) => {
                                        const priceColor =
                                          priceCardColor(price);
                                        return (
                                          <Chip
                                            key={price}
                                            label={`${price} kr`}
                                            sx={{
                                              backgroundColor: `${priceColor}1f`,
                                              border: `1px solid ${priceColor}`,
                                              color: priceColor,
                                              fontSize: '17px',
                                              fontWeight: 700,
                                              height: '40px',
                                              px: 0.5,
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <TouchButton
                                  tone="confirm"
                                  disabled={!hasWinners}
                                  onClick={() => setWinnerModal(row)}
                                  sx={rowButtonSx}
                                >
                                  {hasWinners
                                    ? row.winning.winners.length
                                    : '—'}
                                </TouchButton>
                              </TableCell>
                              <TableCell align="center">
                                <TouchButton
                                  tone="info"
                                  disabled={!finished}
                                  onClick={() => {
                                    handlePrintGame(
                                      row.gameID,
                                      row.name,
                                      row.numbers,
                                      prices
                                    );
                                  }}
                                  sx={{ ...rowButtonSx, minWidth: '170px' }}
                                >
                                  {t('gameplan.printDraw')}
                                </TouchButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default Spillplan;
